/** Booking statuses where the venue has approved the request (organizer may pay). */
import venueDashboardApiService from '../services/venueDashboardApi';
import venueApiService from '../services/venueApi';
import { parseBookingRequirements, bookingHasAddOns } from '../components/VenueDashboard/utils/bookingAddOns';
import { resolveBookingAmounts, resolveBookingBaseAmount } from './bookingAmounts';
import { extractStripeConnectFromVenue } from './stripeConnectStatus';
import {
  FREE_BOOKING_MODE,
  isEntireFreeBooking,
  isSpaceFreeBooking,
  resolveFreeBookingMode,
  resolveChargeableVenueSubtotal,
  resolveChargeableVenueSubtotalFromBooking,
  formatFreeBookingModeLabel,
  buildFreeBookingModeRequirements,
  bookingHasCorruptedRequirements,
  normalizeFreeBookingMode
} from './bookingFreeMode';

export {
  FREE_BOOKING_MODE,
  isEntireFreeBooking,
  isSpaceFreeBooking,
  resolveFreeBookingMode,
  resolveChargeableVenueSubtotal,
  resolveChargeableVenueSubtotalFromBooking,
  formatFreeBookingModeLabel,
  buildFreeBookingModeRequirements,
  bookingHasCorruptedRequirements,
  normalizeFreeBookingMode
};

export const APPROVED_BOOKING_STATUSES = new Set(['confirmed', 'accepted', 'approved']);

export const isApprovedBookingStatus = (status) =>
  APPROVED_BOOKING_STATUSES.has(String(status || '').toLowerCase());

/** Organizer-facing pricing and payment UI (same gate as pay eligibility). */
export const canShowOrganizerBookingPricing = (booking) =>
  Boolean(booking) && isApprovedBookingStatus(booking.status ?? booking.booking_status);

export const isPaidBookingPaymentStatus = (status) => {
  const key = String(status || '').toLowerCase();
  return key === 'paid' || key === 'completed' || key === '1';
};

export const isDepositPaidBookingPaymentStatus = (status) => {
  const key = String(status || '').toLowerCase();
  return key === 'deposit_paid' || key === 'partially_paid';
};

/** Stripe UI on pending requests when venue requires a deposit. */
export const bookingRequiresDepositPayment = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  const req = booking.requirements || {};
  const deposit = req.deposit;
  return Boolean(deposit && typeof deposit === 'object' && deposit.enabled);
};

export const canShowOrganizerStripePayment = (booking, bookingStatus) => {
  if (isApprovedBookingStatus(bookingStatus ?? booking?.status)) return true;
  const status = String(bookingStatus ?? booking?.status ?? '').toLowerCase();
  if (!['pending', 'on_hold', 'pending_review'].includes(status)) return false;
  return bookingRequiresDepositPayment(booking);
};

/** Booking submitted without public pricing — venue sets amount after reviewing the request. */
export const isTbcPricingBooking = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  const req = parseBookingRequirements(booking);
  return Boolean(
    req?.hide_venue_pricing === true ||
      String(req?.pricing_display || '').toUpperCase() === 'TBC' ||
      String(booking?.add_ons_total ?? '').toUpperCase() === 'TBC'
  );
};

/**
 * Booking still needs the venue owner to set a price (or mark free).
 * Includes explicit TBC rows and pending platform requests with zero stored totals.
 */
export const needsVenuePricingConfirmation = (booking) => {
  if (!booking || typeof booking !== 'object') return false;

  if (resolveFreeBookingMode(booking) === FREE_BOOKING_MODE.SPACE_ONLY) {
    return false;
  }

  const req = parseBookingRequirements(booking);

  if (String(req?.pricing_display || '').toUpperCase() === 'CONFIRMED') return false;
  if (req?.hide_venue_pricing === false) return false;

  const status = String(booking.status ?? booking.booking_status ?? 'pending').toLowerCase();
  const { grandTotal, amountPending } = resolveBookingAmounts(booking);
  if (isApprovedBookingStatus(status) && (grandTotal > 0 || amountPending > 0)) {
    return false;
  }

  if (isTbcPricingBooking(booking)) return true;

  if (bookingHasCorruptedRequirements(booking)) return true;

  if (!['pending', 'on_hold'].includes(status)) return false;

  const source = String(
    booking.booking_source ?? booking.source ?? req.source ?? 'platform'
  ).toLowerCase();
  if (source === 'manual' || source === 'venue_dashboard') return false;

  return grandTotal <= 0 && amountPending <= 0;
};

/** True when the venue space hire fee is waived or stored as zero (not unpriced TBC). */
export const bookingHasFreeSpaceFee = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (needsVenuePricingConfirmation(booking)) return false;
  if (isSpaceFreeBooking(booking)) return true;
  return resolveBookingBaseAmount(booking) <= 0;
};

/** Space is waived and there is nothing else to charge. */
export const isExplicitlyFreeBooking = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (bookingHasCorruptedRequirements(booking)) return false;
  if (bookingHasAddOns(booking)) return false;
  return (
    isSpaceFreeBooking(booking) && resolveChargeableVenueSubtotalFromBooking(booking) <= 0
  );
};

/** No organiser charge — free space and no add-ons. */
export const isFreeBooking = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (needsVenuePricingConfirmation(booking)) return false;
  return isExplicitlyFreeBooking(booking);
};

/** Approved booking where the organiser still owes payment. */
export const bookingHasOutstandingBalance = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  const { grandTotal, amountPending } = resolveBookingAmounts(booking);
  return grandTotal > 0 || amountPending > 0;
};

/** Open payment edit mode only when pricing is genuinely unset — not when totals exist. */
export const shouldStartInPaymentEditMode = (booking) => {
  if (!booking || !needsVenuePricingConfirmation(booking)) return false;
  return !bookingHasOutstandingBalance(booking);
};

export const bookingNeedsOrganizerPayment = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (!isApprovedBookingStatus(booking.status ?? booking.booking_status)) return false;
  if (isPaidBookingPaymentStatus(booking.paymentStatus ?? booking.payment_status)) {
    return false;
  }
  const { amountPending, grandTotal } = resolveBookingAmounts(booking);
  return amountPending > 0 || grandTotal > 0;
};

/** Pending request where organiser must pay deposit before the inquiry is complete. */
export const bookingNeedsPendingDepositPayment = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (!bookingRequiresDepositPayment(booking)) return false;
  const status = String(booking.status ?? booking.booking_status ?? '').toLowerCase();
  if (!['pending', 'on_hold', 'pending_review'].includes(status)) return false;
  const paymentStatus = String(booking.paymentStatus ?? booking.payment_status ?? '').toLowerCase();
  if (isDepositPaidBookingPaymentStatus(paymentStatus) || isPaidBookingPaymentStatus(paymentStatus)) {
    return false;
  }
  return true;
};

/** Stripe checkout on booking details (deposit while pending, or balance after approval). */
export const bookingNeedsStripePaymentNow = (booking) => {
  if (!booking || typeof booking !== 'object') return false;
  if (!canShowOrganizerStripePayment(booking, booking.status ?? booking.booking_status)) {
    return false;
  }
  if (isPaidBookingPaymentStatus(booking.paymentStatus ?? booking.payment_status)) {
    return false;
  }
  if (bookingNeedsPendingDepositPayment(booking)) return true;
  return bookingNeedsOrganizerPayment(booking);
};

export const formatPaymentStatusLabel = (status) => {
  const key = String(status || '').toLowerCase();
  const labels = {
    not_started: 'Payment not started',
    pending_method: 'Add payment method',
    scheduled: 'Payment pending',
    method_attached: 'Processing payment',
    processing: 'Processing payment',
    deposit_paid: 'Deposit paid',
    paid: 'Paid',
    failed: 'Payment failed',
    action_required: 'Action required',
    refunded: 'Refunded'
  };
  return labels[key] || (key ? key.replace(/_/g, ' ') : 'Unknown');
};

export const formatChargeAt = (chargeAt) => {
  if (!chargeAt) return null;
  const date = new Date(chargeAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Consumer site origin for organiser payment links (never venues.occasia.io). */
const CONSUMER_SITE_ORIGIN = (
  process.env.REACT_APP_PUBLIC_SITE_URL || 'https://www.occasia.io'
).replace(/\/$/, '');

export const buildBookingPaymentLink = (bookingId) => {
  if (!bookingId) return null;
  return `${CONSUMER_SITE_ORIGIN}/profile/bookings/${encodeURIComponent(String(bookingId))}?pay=stripe`;
};

export const buildBookingDetailsLink = (bookingId) => {
  if (!bookingId) return null;
  return `${CONSUMER_SITE_ORIGIN}/profile/bookings/${encodeURIComponent(String(bookingId))}`;
};

/** Admin toggle: Stripe payments UI enabled for this venue. */
export const venueStripePaymentsFeatureEnabled = (venue) =>
  Boolean(venue?.stripe_payments_enabled);

/** Occasia Stripe checkout: feature enabled and Connect charges are live. */
export const venueOccasiaStripePayEnabled = (venue) => {
  if (venue?.stripe_checkout_enabled != null) {
    return Boolean(venue.stripe_checkout_enabled);
  }
  if (!venueStripePaymentsFeatureEnabled(venue)) return false;
  const connect = extractStripeConnectFromVenue(venue);
  return Boolean(connect?.charges_enabled);
};

const externalLinkContext = (link) => ({
  mode: 'external_link',
  link,
  emailCtaLabel: 'Complete payment',
  emailIntro: 'Please complete payment using the venue payment link below.',
  organiserInstructions: 'Complete payment using the venue payment link.'
});

const stripeLinkContext = (bookingId) => ({
  mode: 'occasia_stripe',
  link: buildBookingPaymentLink(bookingId),
  emailCtaLabel: 'Pay now and add your card',
  emailIntro:
    'Complete payment securely on your Occasia booking page and add your card details.',
  organiserInstructions: 'Pay securely with Stripe and add your card on your booking page.'
});

const invoiceLinkContext = (bookingId) => ({
  mode: 'invoice',
  link: buildBookingDetailsLink(bookingId),
  emailCtaLabel: 'View booking and download invoice',
  emailIntro:
    'Pay the venue directly using the payment details on the uploaded invoice. Download the invoice from your booking page.',
  organiserInstructions:
    'Download the invoice from your booking page and pay the venue directly using the details on the uploaded invoice.'
});

const freeBookingContext = (bookingId) => ({
  mode: 'free',
  link: buildBookingDetailsLink(bookingId),
  emailCtaLabel: 'View booking details',
  emailIntro: 'Your booking is confirmed. No payment is required.',
  organiserInstructions: 'No payment is required for this booking.'
});

export const paymentEmailFeedbackMessage = (mode) => {
  if (mode === 'free') {
    return 'Confirmation email sent — no payment link included.';
  }
  if (mode === 'occasia_stripe') {
    return 'Payment email sent with a secure Stripe link to pay and add card details.';
  }
  if (mode === 'external_link') {
    return 'Payment email sent with the venue payment link.';
  }
  return 'Payment email sent with instructions to pay using the uploaded invoice.';
};

/** Params for send-booking-approved-email / payment reminder APIs. */
export const buildApprovalEmailParams = (paymentContext) => {
  const params = {};
  if (paymentContext.mode && paymentContext.mode !== 'free') {
    params.payment_mode = paymentContext.mode;
  }
  if (paymentContext.mode !== 'free' && paymentContext.link) {
    params.payment_link = paymentContext.link;
  }
  return params;
};

export const normalizeBookingIdentity = (value) =>
  value == null ? '' : String(value).trim().toLowerCase();

/** Customer / organiser email stored on the booking (guest email preferred). */
export const getBookingCustomerEmail = (booking) => {
  if (!booking || typeof booking !== 'object') return '';
  const req = parseBookingRequirements(booking);
  return normalizeBookingIdentity(
    booking.contactEmail ||
      req?.customer_info?.email ||
      booking.contact_email ||
      booking.customer_email ||
      ''
  );
};

/** True when the signed-in user is the booking customer / organiser. */
export const loggedInUserOwnsBooking = (booking, { userProfile, currentUser } = {}) => {
  if (!booking || typeof booking !== 'object') return false;

  const userEmail = normalizeBookingIdentity(currentUser?.email || userProfile?.email);
  const userIdStr = String(
    userProfile?.backend_user_id || userProfile?.firebase_uid || currentUser?.uid || ''
  );
  if (!userEmail && !userIdStr) return false;

  const guestEmail = getBookingCustomerEmail(booking);
  const customerId = booking.customer_id || booking.customerId;
  const eventOrganizerId = booking.event_organizer_id || booking.eventOrganizerId;
  const isManual =
    String(booking.bookingSource || booking.booking_source || '').toLowerCase() === 'manual';

  if (userEmail && guestEmail && userEmail === guestEmail) return true;
  if (eventOrganizerId && userIdStr && String(eventOrganizerId) === userIdStr) return true;
  if (customerId && userIdStr && String(customerId) === userIdStr) {
    if (isManual && userEmail && guestEmail && guestEmail !== userEmail) return false;
    return true;
  }
  return false;
};

/**
 * Whether the current session may view this booking.
 * Admins pass when `adminViewUserId` matches the booking organiser (id or email).
 */
export const canAccessBooking = (booking, { userProfile, currentUser, adminViewUserId } = {}) => {
  const isAdmin =
    userProfile?.is_admin === true ||
    userProfile?.role === 'admin' ||
    userProfile?.user_type === 'admin';

  if (isAdmin && adminViewUserId) {
    const viewKey = String(adminViewUserId).trim();
    const viewEmail = viewKey.includes('@') ? normalizeBookingIdentity(viewKey) : '';
    const viewId = viewEmail ? '' : viewKey;
    const customerId = String(booking?.customer_id || booking?.customerId || '');
    const eventOrganizerId = String(booking?.event_organizer_id || booking?.eventOrganizerId || '');
    const guestEmail = getBookingCustomerEmail(booking);

    if (viewEmail && guestEmail && viewEmail === guestEmail) return true;
    if (viewId && (viewId === customerId || viewId === eventOrganizerId)) return true;
    return false;
  }

  return loggedInUserOwnsBooking(booking, { userProfile, currentUser });
};

/** How many manual payment reminder emails have been sent for this booking. */
export const getPaymentReminderSentCount = (booking) => {
  if (!booking || typeof booking !== 'object') return 0;
  const req = parseBookingRequirements(booking);
  const raw = req.payment_reminder_sent_count ?? booking.payment_reminder_sent_count;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

/** How many post-event feedback request emails have been sent for this booking. */
export const getFeedbackRequestSentCount = (booking) => {
  if (!booking || typeof booking !== 'object') return 0;
  const req = parseBookingRequirements(booking);
  const raw = req.feedback_request_sent_count ?? booking.feedback_request_sent_count;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

/** After invoice upload, only invoice/external flows need a follow-up email (not Stripe or free). */
export const shouldSendPaymentEmailAfterInvoiceUpload = (mode) =>
  mode !== 'occasia_stripe' && mode !== 'free';

/**
 * Resolve how an organiser should pay.
 * 1. Stripe when admin feature is on AND Connect charges are enabled.
 * 2. Else venue payment_link when Stripe is off and link is set.
 * 3. Else invoice (pay using uploaded invoice details).
 */
export const resolveBookingPaymentContext = async (
  bookingId,
  venueId,
  venuePaymentLinkHint = null,
  { forceStripePay = false, booking = null } = {}
) => {
  if (booking && isFreeBooking(booking) && !bookingHasAddOns(booking)) {
    return freeBookingContext(bookingId);
  }

  if (forceStripePay) {
    return stripeLinkContext(bookingId);
  }

  let venue = null;
  if (venueId) {
    try {
      venue = await venueDashboardApiService.getVenueDetails(venueId);
    } catch {
      try {
        venue = await venueApiService.getVenueById(venueId);
      } catch {
        venue = null;
      }
    }
  }

  if (venue && venueOccasiaStripePayEnabled(venue)) {
    return stripeLinkContext(bookingId);
  }

  const custom = String(
    venuePaymentLinkHint || venue?.payment_link || venue?.payment_url || ''
  ).trim();
  if (custom) {
    return externalLinkContext(custom);
  }

  return invoiceLinkContext(bookingId);
};

/** Prefer venue-configured payment URL; fall back to Occasia booking payment page or invoice flow. */
export const resolveBookingPaymentLink = async (bookingId, venueId, venuePaymentLinkHint = null) => {
  const context = await resolveBookingPaymentContext(bookingId, venueId, venuePaymentLinkHint);
  return context.link;
};
